/* eslint semi:0 */
/**
 * PDO is our mechanism to pass site, page, and configuration data to
 * the client. It is passed in the page head as a script with mimetype
 * "application/json". Here we read, parse it and add methods based on
 * it's payload.
 */
import { error } from './console'
import objectPath from 'object-path'
import { registerInNamespace } from './nm'
import { create as makeEmptyObj } from '../utils/object'
import { adsConfig, ADS_QUERYSTRING_OVERRIDE_PREFIXES } from '../organizedByType/config/adsConfig'
import { queryString, getPossiblyOverriddenPropByQsWithPrefix } from '../utils/window'

const PAGE_DATA_OBJECT_ID = '_page_data_object'
let pageDataObject = null
const pageDataObjectEl = document.getElementById(PAGE_DATA_OBJECT_ID) // Don't import utils/dom to avoid circular dependencies
const pageDataObjectTextcontent = pageDataObjectEl && pageDataObjectEl.textContent

try {
  pageDataObject = Object.assign(makeEmptyObj(), JSON.parse(pageDataObjectTextcontent))
} catch (ex) {
  error("Can't read or parse pageDataObject:", ex, {
    ex,
    pageDataObjectEl,
    id: PAGE_DATA_OBJECT_ID,
    pageDataObjectTextcontent,
  })
}

export const objectPathPdo = objectPath(pageDataObject)
const { get: getFromPdo } = objectPathPdo
export const get = getFromPdo

const adsConfPath = getFromPdo('page.ads_conf', {
  dfp_client_id: '175840252', // eslint-disable-line camelcase
})
const shouldShowAds = 'should_show_ads?' in adsConfPath ? adsConfPath['should_show_ads?'] : true // show ads by default!
const { original: adsConfOriginal = {} } = adsConfPath
const adsConfOriginalPath = {
  clientId: adsConfOriginal.dfp_client_id,
  property: adsConfOriginal.property,
  channel: adsConfOriginal.channel,
  pageType: adsConfOriginal.page_type,
}
const postLayout = getFromPdo('post.layout')
const POST_LAYOUTS = {
  EMBED: 'embed',
  NATIVE: 'native',
  HYBRID: 'hybrid',
  FULL: 'full',
  DRAFT: 'preview',
}

const HAS_BOTTOM_WIDGET_INDICATOR_LABEL_NAME = 'bottom-embed'

export const postTemplate = getFromPdo('post.template')
const POST_TEMPLATES = {
  SLIDESHOW: 'slideshow',
  READER: 'reader',
  TV: 'tv',
  TOPX: 'topx',
  ARTICLE: 'article',
}

export const excludedComponents = getFromPdo('config.excluded_components', [])

const ENVIRONMENT_NAMES = {
  PROD: 'production',
  DEV: 'development',
}

let postCategoriesSet

export function getIsEmbededPost() {
  return postLayout === POST_LAYOUTS.EMBED
}

export function getEnvironment() {
  return getFromPdo('config.env', ENVIRONMENT_NAMES.PROD)
}

export function getIsQaEnv() {
  const { DEV, PROD } = ENVIRONMENT_NAMES
  return ![DEV, PROD].includes(getEnvironment())
}

export function getIsTopX() {
  return postTemplate === POST_TEMPLATES.TOPX
}

export function getIsTv() {
  return postTemplate === POST_TEMPLATES.TV
}

export function getIsSlideshow() {
  return postTemplate === POST_TEMPLATES.SLIDESHOW
}

export function getIsPostReader() {
  return postTemplate === POST_TEMPLATES.READER
}

export function getIsArticle() {
  return postTemplate === POST_TEMPLATES.ARTICLE
}

export function getIsDraft() {
  return postLayout === POST_LAYOUTS.DRAFT
}

export function getIsNative() {
  return postLayout === POST_LAYOUTS.NATIVE
}

export function getIsHybrid() {
  return postLayout === POST_LAYOUTS.HYBRID
}

export function getIsFull() {
  return postLayout === POST_LAYOUTS.FULL
}

export function getHasBottomWidgetIndicator() {
  return getPostCategories().includes(HAS_BOTTOM_WIDGET_INDICATOR_LABEL_NAME)
}

export function getPostUrl() {
  return getFromPdo('post.url')
}

export function getLeaguesForFixtures() {
  return getFromPdo('config.leagues_for_edition.edition_legaues', {})
}

export function getCampaigns() {
  return getFromPdo('config.campaigns', {})
}

export function getIsPostPage() {
  return getFromPdo('page.type') === 'post'
}

export function getPageType() {
  return getFromPdo('page.type')
}

export function getRequestUrl() {
  return getFromPdo('page.requestUrl')
}

export function getPageLocale() {
  return getFromPdo('page.locale')
}

export function getPageDomain() {
  return getFromPdo('post.author.site')
}

export function getPostAuthorId() {
  return getFromPdo('post.author.id')
}

export function getSiteDomainWithTld() {
  return getFromPdo('context.domain')
}

export function getReactionsList() {
  return getFromPdo('post.reactions')
}

export function getNextPosts() {
  return getFromPdo('post.next_posts')
}
export function getSite() {
  return getFromPdo('context.site')
}

export function getPostCategoriesSet() {
  return (
    postCategoriesSet ||
    (postCategoriesSet = (function lazyGetPostCategoriesSet() {
      const { overridePostCategories } = queryString
      const fakeCategories = overridePostCategories && overridePostCategories.split(',')
      return new Set(fakeCategories || getFromPdo('post.categories', []))
    })())
  )
}

export function getPostCategories() {
  return [...getPostCategoriesSet()]
}

export function getPostId() {
  return getFromPdo('post.id', 0)
}

export function getPostTitle() {
  return getFromPdo('post.title', '') || ''
}

export function getDistributionChannels() {
  return getFromPdo('post.distribution_channels', [])
}

export function isSupportedForDomain(componentName) {
  return !excludedComponents.includes(componentName)
}

export const ads = {
  adsConfPath,
  clientId: adsConfPath.dfp_client_id,
  property: adsConfPath.property,
  channel: adsConfPath.channel,
  pageType: adsConfPath.page_type,
  original: adsConfOriginalPath,
  shouldShowAds,
  isProSeven: (shouldShowAds && adsConfPath['is_pro7?']) || false,
  getPropWithQsOverride: getPossiblyOverriddenPropByQsWithPrefix.bind(
    adsConfOriginalPath,
    ADS_QUERYSTRING_OVERRIDE_PREFIXES
  ),

  get hierarchyLevels() {
    const { getPropWithQsOverride, clientId, property, channel, pageType } = this
    let staticProps = {
      delimiter: '/',
      l0: getPropWithQsOverride('clientId', clientId),
      l1: getPropWithQsOverride('property', property),
      l2: getPropWithQsOverride('channel', channel),
      l3: getPropWithQsOverride('pageType', pageType),
      format: 'DefaultFormat',
      toString: function adLevelsToString() {
        const { l0, l1, l2, l3, l4, delimiter } = this
        return [l0, l1, l2, l3, l4].join(delimiter)
      },
      get mainLevels() {
        return Object.make(
          {
            toString: function mainLevelsToString() {
              const { property, channel, pageType } = this // eslint-disable-line no-shadow
              return [property, channel, pageType].join(staticProps.delimiter)
            },
          },
          {
            property: this.l1,
            channel: this.l2,
            pageType: this.l3,
          }
        )
      },
    }
    return Object.define(staticProps, {
      set l4(format) {
        this.format = format
      },
      get l4() {
        return this.formatWithQsOverride
      },
      get formatWithQsOverride() {
        const { format } = this
        return getPropWithQsOverride(`format-${format}`, format)
      },
    })
  },
}

export function getPostMediaTypes() {
  return getFromPdo('post.template_data.media_type', [])
}

export function getPostMainMediaType() {
  return getPostMediaTypes()[0] || null
}

export function getPostBodyMediaTypes() {
  return getPostMediaTypes().slice(1)
}

export function getIsPostMainMediaImage() {
  return (getPostMainMediaType() || '').match(/image/i)
}

export function getPostActionsPath() {
  return getFromPdo('post.admin_options_path')
}

export function getPostActionsFromPdo() {
  return getFromPdo('post.post_actions')
}

export function getApiServicesEndpoints() {
  return getFromPdo('config.api')
}

let publicPdo = makeEmptyObj({
  getIsEmbededPost,
  postTemplate,
  getEnvironment,
  getIsQaEnv,
  getIsTopX,
  getIsTv,
  getIsSlideshow,
  getIsPostReader,
  getIsDraft,
  getIsNative,
  getIsHybrid,
  getIsFull,
  getLeaguesForFixtures,
  getCampaigns,
  getIsPostPage,
  getPageType,
  getRequestUrl,
  getPageLocale,
  getPageDomain,
  getReactionsList,
  getPostCategoriesSet,
  getPostCategories,
  getPostId,
  isSupportedForDomain,
  ads,
  getPostMediaTypes,
  getPostMainMediaType,
  getPostBodyMediaTypes,
  getIsPostMainMediaImage,
  getSiteDomainWithTld,
})

publicPdo = Object.assign(publicPdo, objectPathPdo)
if (!publicPdo.has('config.ads')) {
  publicPdo.set('config.ads', adsConfig)
}

// TODO: Render adsConfig on server

registerInNamespace({ pdo: publicPdo })

export function getRecommendedPosts() {
  return getNextPosts()
  // const posts = [
  //   {
  //     image_url:
  //       'https://images0.minutemediacdn.com/development/912x516/593fbf8bbc46c820ff000006.jpg',
  //     title: 'Sat oh the cuddled and that pithily since chameleon regretful',
  //     author_name: 'Rocky Balboa',
  //     url:
  //       'http://www.90min.com/posts/5740008-inter-working-on-deal-to-sign-jiangsu-suning-midfielder-ramires-ahead-of-transfer-january',
  //   },
  //   {
  //     image_url:
  //       'https://images1.minutemediacdn.com/production/912x516/59f6fc432ee40f73a8000001.jpg',
  //     title: 'No Choice: Morata Reveals He Did Not Want to Leave Juventus for Real Madrid Return',
  //     author_name: 'Rocky Balboa',
  //     url:
  //       'http://www.90min.com/posts/5739898-no-choice-morata-reveals-he-did-not-want-to-leave-juventus-for-real-madrid-return',
  //   },
  //   {
  //     image_url:
  //       'https://images0.minutemediacdn.com/production/912x516/59f6f7c6d915b204e1000003.jpg',
  //     title:
  //       'PHOTO: Increasingly Desperate ISIS at it Again After Photoshopping Neymar & Messi into New Poster',
  //     author_name: 'Rocky Balboa',
  //     url:
  //       'http://www.90min.com/posts/5739792-photo-increasingly-desperate-isis-at-it-again-after-photoshopping-neymar-messi-into-new-poster',
  //   },
  // ]
  // return posts
}

export default publicPdo



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/application/pageDataObject.js